import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NzModalService } from 'ng-zorro-antd/modal';
import { firstValueFrom } from 'rxjs';
import { AppModule } from '../../app.module';
import { MapSelect } from '../../map/map-select/map-select';
import { Client, ClientLink, IncidentClientLink } from '../../models/client';
import { Incident, IncidentLocation } from '../../models/incident';
import { ClientType } from '../../shared/enums';
import { Globals } from '../../shared/functions';
import { IncidentService } from '../../shared/incident.service';
import { FleetBreakdownNavigationService } from '../../shared/fleet-breakdown-navigation.service';

@Component({
  selector: 'app-fleet-new-breakdown',
  imports: [AppModule, MapSelect],
  templateUrl: './fleet-new-breakdown.html',
  styleUrl: './fleet-new-breakdown.less'
})
export class FleetNewBreakdown implements OnInit {
  public isCvvValid = true;
  public isLoadingCallcentre = false;
  public callcentreLoadFailed = false;
  public callcentre: Client | null = null;
  public incident = new Incident();
  public locationLink = '';
  public isDecodingLocation = false;
  public isSavingIncident = false;
  public saveFailed = false;

  private readonly geocoder = new google.maps.Geocoder();
  private fleetId = 0;
  private callcentreId = 0;
  private cvv = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private modal: NzModalService,
    private incidentService: IncidentService,
    private fleetBreakdownNavigation: FleetBreakdownNavigationService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const fleetId = Number(params['fleetId']);
      const callcentreId = Number(params['callcentreId']);

      this.isCvvValid = Number.isInteger(fleetId)
        && fleetId > 0
        && Number.isInteger(callcentreId)
        && callcentreId > 0
        && Globals.Functions.isFleetCvvValid(fleetId, callcentreId, params['cvv']);

      if (!this.isCvvValid) {
        return;
      }

      this.incident = new Incident();
      this.incident.clientId = fleetId;
      this.fleetId = fleetId;
      this.callcentreId = callcentreId;
      this.cvv = params['cvv'];
      this.loadCallcentre(callcentreId);
    });
  }

  public onLocationSelected(location: IncidentLocation): void {
    this.incident.location = location;
    this.saveFailed = false;
  }

  public onClickLogBreakdown(): void {
    if (!this.incident.location || this.isSavingIncident) {
      return;
    }

    this.isSavingIncident = true;
    this.saveFailed = false;

    this.incidentService.addOrUpdateFleetIncidentNoToken(this.incident, 5).subscribe({
      next: () => {
        this.navigateToBreakdowns();
      },
      error: () => {
        this.saveFailed = true;
        this.isSavingIncident = false;
      }
    });
  }

  public onViewBreakdowns(): void {
    if (this.incident.location) {
      this.modal.confirm({
        nzTitle: 'Leave this breakdown?',
        nzContent: 'Your selected location has not been logged yet.',
        nzOkText: 'View breakdowns',
        nzCancelText: 'Stay here',
        nzOnOk: () => this.navigateToBreakdowns()
      });
      return;
    }

    this.navigateToBreakdowns();
  }

  public async onClickDecodeLocationLink(): Promise<void> {
    if (!this.locationLink.trim()) {
      return;
    }

    this.isDecodingLocation = true;

    try {
      const [latitude, longitude] = await this.getLatLongFromUrl(this.locationLink);
      if (latitude === null || longitude === null) {
        return;
      }

      this.incident.location = {
        latitude,
        longitude,
        description: null,
        descriptionShort: ''
      };
      this.geocodeLocation(new google.maps.LatLng(latitude, longitude));
    } finally {
      this.isDecodingLocation = false;
    }
  }

  private loadCallcentre(callcentreId: number): void {
    this.isLoadingCallcentre = true;
    this.callcentreLoadFailed = false;
    this.callcentre = null;

    this.incidentService.getCallcentreNoToken(callcentreId).subscribe({
      next: (callcentre) => {
        this.callcentre = callcentre;
        this.addCallcentreToIncident(callcentre);
        this.isLoadingCallcentre = false;
      },
      error: () => {
        this.callcentreLoadFailed = true;
        this.isLoadingCallcentre = false;
      }
    });
  }

  private navigateToBreakdowns(): void {
    const fleetCvv = Globals.Functions.createFleetCvv(this.fleetId, 0);
    this.fleetBreakdownNavigation.setReturnContext({
      fleetId: this.fleetId,
      callcentreId: this.callcentreId,
      cvv: this.cvv
    });
    this.router.navigate(['/breakdowns', this.fleetId, fleetCvv]);
  }

  private addCallcentreToIncident(callcentre: Client): void {
    const clientLink: ClientLink = {
      clientId: callcentre.clientId,
      clientName: callcentre.clientName,
      clientTel: callcentre.clientTel,
      clientCell: callcentre.clientCell ?? '',
      connectionTypeId: ClientType.call_centre,
      latitude: callcentre.latitude,
      longitude: callcentre.longitude
    };
    const callcentreLink = new IncidentClientLink(clientLink);
    callcentreLink.clientTel = clientLink.clientTel;
    callcentreLink.clientCell = clientLink.clientCell;
    callcentreLink.isActive = true;

    this.incident.incidentClients = [callcentreLink];
  }

  private geocodeLocation(latLng: google.maps.LatLng): void {
    this.geocoder.geocode({ location: latLng }, (results, status) => {
      if (status !== 'OK' || !results?.[0] || !this.incident.location) {
        return;
      }

      this.incident.location.description = results[0].formatted_address;
      this.incident.location.descriptionShort = results[0].address_components
        .filter(component => component.types.includes('sublocality_level_1')
          || component.types.includes('locality')
          || component.types.includes('administrative_area_level_1'))
        .map(component => component.types.includes('administrative_area_level_1')
          ? component.short_name
          : component.long_name)
        .join(', ');
    });
  }

  private async getLatLongFromUrl(url: string): Promise<[number | null, number | null]> {
    try {
      url = url.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));

      if (/goo\.gl|maps\.app\.goo\.gl/.test(url)) {
        url = (await firstValueFrom(this.incidentService.expandUrl(url))).url;
      }

      const coordinatePatterns = [
        /[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/,
        /@(-?\d+\.\d+),(-?\d+\.\d+)/,
        /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/,
        /[?&]query=(-?\d+\.\d+),(-?\d+\.\d+)/
      ];
      const coordinateMatch = coordinatePatterns
        .map(pattern => url.match(pattern))
        .find(match => match !== null);

      if (coordinateMatch) {
        return [parseFloat(coordinateMatch[1]), parseFloat(coordinateMatch[2])];
      }

      const addressMatch = url.match(/\/place\/([^/]+)/)
        ?? url.match(/\/search\/([^/]+)/)
        ?? url.match(/[?&]q=([^&]+)/);

      if (!addressMatch) {
        return [null, null];
      }

      return await this.geocodeAddress(decodeURIComponent(addressMatch[1].replace(/\+/g, ' ')));
    } catch {
      return [null, null];
    }
  }

  private geocodeAddress(address: string): Promise<[number | null, number | null]> {
    return new Promise(resolve => {
      this.geocoder.geocode({ address }, (results, status) => {
        if (status !== 'OK' || !results?.[0]) {
          resolve([null, null]);
          return;
        }

        const location = results[0].geometry.location;
        resolve([location.lat(), location.lng()]);
      });
    });
  }
}
